/**
 * FSM Registry (Atlas 3.0 §L2 — State Machines).
 *
 * Static registry of the 6 state machines in the Brisarx product. Each
 * machine definition includes: states, transitions, invariants, safety tier,
 * and sandbox mode flag. Visualization is done in StateMachineView.tsx using
 * pure CSS/SVG — this module is pure data (no UI deps).
 *
 * Machines:
 *   checkoutMachine       — Tier 0, sandboxed (payment flow)
 *   phoneOTPMachine       — Tier 0, sandboxed (auth flow)
 *   cancellationMachine   — Tier 1, sandboxed (subscription cancel)
 *   migrationWizardMachine— Tier 1, read-only (data migration)
 *   onboardingMachine     — Tier 1, sandboxed (new user onboarding)
 *   consentMachine        — Tier 0, sandboxed (GDPR consent)
 */
import type { EventSafetyTier } from '@atlas-manifest/types';

// ─── State machine types ──────────────────────────────────────────────────────

export interface FSMState {
  id: string;
  label: string;
  /** Terminal states have no outgoing transitions. */
  isFinal: boolean;
  /** Entry states (at most one per machine). */
  isInitial: boolean;
  description?: string;
}

export interface FSMTransition {
  id: string;
  from: string;
  to: string;
  /** Event / action name that triggers this transition. */
  event: string;
  /** Guard condition expression (human-readable). */
  guard?: string;
  /** Whether this transition is reversible. */
  reversible: boolean;
  /** Safety classification of this transition. */
  safetyTier: EventSafetyTier;
  description?: string;
}

export interface FSMInvariant {
  id: string;
  description: string;
  /** States where this invariant must hold. */
  applyInStates: string[];
  /** Machine-readable expression (for documentation). */
  expression?: string;
}

export interface FSMDefinition {
  id: string;
  label: string;
  description: string;
  /** Source file path relative to repo root. */
  sourceFile: string;
  /** Overall machine safety tier. */
  safetyTier: EventSafetyTier;
  /** Whether the machine can be safely simulated without side effects. */
  sandboxed: boolean;
  /** Whether the machine is read-only (no mutations allowed in simulation). */
  readOnly: boolean;
  states: FSMState[];
  transitions: FSMTransition[];
  invariants: FSMInvariant[];
  /** Event types this machine emits. */
  emitsEvents: string[];
  /** Compliance refs. */
  complianceRefs: string[];
}

// ─── Machine definitions ──────────────────────────────────────────────────────

const checkoutMachine: FSMDefinition = {
  id: 'checkoutMachine',
  label: 'Checkout Machine',
  description: 'Stripe subscription checkout flow. Manages the payment intent lifecycle from plan selection to confirmation.',
  sourceFile: 'src/app/machines/checkoutMachine.ts',
  safetyTier: 0,
  sandboxed: true,
  readOnly: false,
  emitsEvents: ['subscription_started', 'payment_succeeded', 'payment_failed'],
  complianceRefs: ['CFPB 1033', 'PCI-DSS'],
  states: [
    { id: 'idle', label: 'Idle', isInitial: true, isFinal: false, description: 'No active checkout session.' },
    { id: 'plan_selected', label: 'Plan Selected', isInitial: false, isFinal: false },
    { id: 'payment_pending', label: 'Payment Pending', isInitial: false, isFinal: false, description: 'Stripe payment intent created, awaiting confirmation.' },
    { id: 'payment_confirming', label: 'Confirming', isInitial: false, isFinal: false },
    { id: 'success', label: 'Success', isInitial: false, isFinal: true },
    { id: 'failed', label: 'Failed', isInitial: false, isFinal: true },
    { id: 'cancelled', label: 'Cancelled', isInitial: false, isFinal: true },
  ],
  transitions: [
    { id: 't1', from: 'idle', to: 'plan_selected', event: 'SELECT_PLAN', reversible: true, safetyTier: 2 },
    { id: 't2', from: 'plan_selected', to: 'payment_pending', event: 'INITIATE_PAYMENT', reversible: false, safetyTier: 0, description: 'Creates Stripe PaymentIntent.' },
    { id: 't3', from: 'payment_pending', to: 'payment_confirming', event: 'CONFIRM_PAYMENT', reversible: false, safetyTier: 0 },
    { id: 't4', from: 'payment_confirming', to: 'success', event: 'PAYMENT_SUCCEEDED', reversible: false, safetyTier: 0 },
    { id: 't5', from: 'payment_confirming', to: 'failed', event: 'PAYMENT_FAILED', reversible: false, safetyTier: 0 },
    { id: 't6', from: 'plan_selected', to: 'cancelled', event: 'CANCEL', reversible: false, safetyTier: 2 },
    { id: 't7', from: 'payment_pending', to: 'cancelled', event: 'CANCEL', reversible: false, safetyTier: 2, guard: 'PaymentIntent not yet confirmed' },
  ],
  invariants: [
    { id: 'inv1', description: 'Raw card numbers must never appear in state payload.', applyInStates: ['payment_pending', 'payment_confirming'], expression: '!state.payload.cardNumber && !state.payload.cvv' },
    { id: 'inv2', description: 'PaymentIntent ID must be set before confirming.', applyInStates: ['payment_confirming'], expression: 'state.context.paymentIntentId !== undefined' },
  ],
};

const phoneOTPMachine: FSMDefinition = {
  id: 'phoneOTPMachine',
  label: 'Phone OTP Machine',
  description: 'Phone-first progressive identity authentication. Manages OTP send, verify, and retry lifecycle.',
  sourceFile: 'src/app/machines/phoneOTPMachine.ts',
  safetyTier: 0,
  sandboxed: true,
  readOnly: false,
  emitsEvents: ['user_authenticated', 'otp_verification_failed'],
  complianceRefs: ['GDPR Art.6', 'CCPA §1798.100'],
  states: [
    { id: 'idle', label: 'Idle', isInitial: true, isFinal: false },
    { id: 'phone_entered', label: 'Phone Entered', isInitial: false, isFinal: false },
    { id: 'otp_sent', label: 'OTP Sent', isInitial: false, isFinal: false },
    { id: 'otp_verifying', label: 'Verifying OTP', isInitial: false, isFinal: false },
    { id: 'authenticated', label: 'Authenticated', isInitial: false, isFinal: true },
    { id: 'failed', label: 'Max Retries Exceeded', isInitial: false, isFinal: true },
  ],
  transitions: [
    { id: 't1', from: 'idle', to: 'phone_entered', event: 'ENTER_PHONE', reversible: true, safetyTier: 2 },
    { id: 't2', from: 'phone_entered', to: 'otp_sent', event: 'SEND_OTP', reversible: false, safetyTier: 1, description: 'Triggers Firebase Auth SMS.' },
    { id: 't3', from: 'otp_sent', to: 'otp_verifying', event: 'SUBMIT_OTP', reversible: false, safetyTier: 1 },
    { id: 't4', from: 'otp_verifying', to: 'authenticated', event: 'OTP_VALID', reversible: false, safetyTier: 0 },
    { id: 't5', from: 'otp_verifying', to: 'otp_sent', event: 'OTP_INVALID', reversible: true, safetyTier: 2, guard: 'retries < 3' },
    { id: 't6', from: 'otp_verifying', to: 'failed', event: 'OTP_INVALID', reversible: false, safetyTier: 2, guard: 'retries >= 3' },
    { id: 't7', from: 'otp_sent', to: 'phone_entered', event: 'RESEND_OTP', reversible: true, safetyTier: 2, guard: 'cooldown expired' },
  ],
  invariants: [
    { id: 'inv1', description: 'Raw OTP code must never persist in state.', applyInStates: ['otp_verifying', 'authenticated'], expression: '!state.context.otpCode' },
    { id: 'inv2', description: 'Retry count must not exceed 3.', applyInStates: ['otp_sent', 'otp_verifying'], expression: 'state.context.retries <= 3' },
  ],
};

const cancellationMachine: FSMDefinition = {
  id: 'cancellationMachine',
  label: 'Cancellation Machine',
  description: 'Subscription cancellation flow with retention offer interception and GDPR deletion request gate.',
  sourceFile: 'src/app/machines/cancellationMachine.ts',
  safetyTier: 1,
  sandboxed: true,
  readOnly: false,
  emitsEvents: ['subscription_cancelled', 'retention_offer_accepted'],
  complianceRefs: ['GDPR Art.17', 'CCPA §1798.105'],
  states: [
    { id: 'idle', label: 'Idle', isInitial: true, isFinal: false },
    { id: 'reason_capture', label: 'Capturing Reason', isInitial: false, isFinal: false },
    { id: 'retention_offer', label: 'Retention Offer', isInitial: false, isFinal: false },
    { id: 'confirming', label: 'Confirming Cancellation', isInitial: false, isFinal: false },
    { id: 'cancelled', label: 'Cancelled', isInitial: false, isFinal: true },
    { id: 'retained', label: 'Retained', isInitial: false, isFinal: true },
  ],
  transitions: [
    { id: 't1', from: 'idle', to: 'reason_capture', event: 'BEGIN_CANCEL', reversible: true, safetyTier: 2 },
    { id: 't2', from: 'reason_capture', to: 'retention_offer', event: 'SUBMIT_REASON', reversible: true, safetyTier: 2 },
    { id: 't3', from: 'retention_offer', to: 'retained', event: 'ACCEPT_OFFER', reversible: false, safetyTier: 1 },
    { id: 't4', from: 'retention_offer', to: 'confirming', event: 'DECLINE_OFFER', reversible: true, safetyTier: 2 },
    { id: 't5', from: 'confirming', to: 'cancelled', event: 'CONFIRM_CANCEL', reversible: false, safetyTier: 1, description: 'Triggers Stripe cancel + GDPR data deletion gate.' },
    { id: 't6', from: 'confirming', to: 'idle', event: 'ABORT', reversible: true, safetyTier: 2 },
  ],
  invariants: [
    { id: 'inv1', description: 'Cancellation must not proceed without a captured reason.', applyInStates: ['confirming'], expression: 'state.context.reason !== undefined' },
  ],
};

const migrationWizardMachine: FSMDefinition = {
  id: 'migrationWizardMachine',
  label: 'Migration Wizard Machine',
  description: 'Step-by-step data migration wizard for account consolidation and format upgrades. Read-only simulation mode.',
  sourceFile: 'src/app/machines/migrationWizardMachine.ts',
  safetyTier: 1,
  sandboxed: false,
  readOnly: true,
  emitsEvents: ['migration_completed', 'migration_aborted'],
  complianceRefs: ['GDPR Art.20'],
  states: [
    { id: 'idle', label: 'Idle', isInitial: true, isFinal: false },
    { id: 'source_selection', label: 'Select Source', isInitial: false, isFinal: false },
    { id: 'preview', label: 'Preview Changes', isInitial: false, isFinal: false },
    { id: 'validation', label: 'Validating', isInitial: false, isFinal: false },
    { id: 'executing', label: 'Executing', isInitial: false, isFinal: false },
    { id: 'completed', label: 'Completed', isInitial: false, isFinal: true },
    { id: 'failed', label: 'Failed', isInitial: false, isFinal: true },
    { id: 'aborted', label: 'Aborted', isInitial: false, isFinal: true },
  ],
  transitions: [
    { id: 't1', from: 'idle', to: 'source_selection', event: 'BEGIN', reversible: true, safetyTier: 2 },
    { id: 't2', from: 'source_selection', to: 'preview', event: 'SELECT_SOURCE', reversible: true, safetyTier: 2 },
    { id: 't3', from: 'preview', to: 'validation', event: 'PROCEED', reversible: false, safetyTier: 1 },
    { id: 't4', from: 'validation', to: 'executing', event: 'VALIDATION_PASSED', reversible: false, safetyTier: 1 },
    { id: 't5', from: 'validation', to: 'failed', event: 'VALIDATION_FAILED', reversible: false, safetyTier: 1 },
    { id: 't6', from: 'executing', to: 'completed', event: 'MIGRATION_DONE', reversible: false, safetyTier: 1 },
    { id: 't7', from: 'executing', to: 'failed', event: 'MIGRATION_FAILED', reversible: false, safetyTier: 1 },
    { id: 't8', from: 'preview', to: 'aborted', event: 'ABORT', reversible: false, safetyTier: 2 },
  ],
  invariants: [
    { id: 'inv1', description: 'Preview must show all affected entities before execution.', applyInStates: ['preview'], expression: 'state.context.affectedEntities.length > 0' },
  ],
};

const onboardingMachine: FSMDefinition = {
  id: 'onboardingMachine',
  label: 'Onboarding Machine',
  description: 'New user onboarding flow (RedesignedOnboarding v6). 9 steps with skip logic. Phone-first identity.',
  sourceFile: 'src/app/machines/onboardingMachine.ts',
  safetyTier: 1,
  sandboxed: true,
  readOnly: false,
  emitsEvents: ['onboarding_completed', 'onboarding_step_completed', 'profile_created'],
  complianceRefs: ['GDPR Art.13', 'COPPA §312', 'KOSA §3'],
  states: [
    { id: 'welcome', label: 'Welcome', isInitial: true, isFinal: false },
    { id: 'phone_auth', label: 'Phone Auth', isInitial: false, isFinal: false },
    { id: 'profile', label: 'Profile', isInitial: false, isFinal: false },
    { id: 'consent', label: 'Consent', isInitial: false, isFinal: false },
    { id: 'age_gate', label: 'Age Gate', isInitial: false, isFinal: false },
    { id: 'goals', label: 'Goals Setup', isInitial: false, isFinal: false },
    { id: 'connect_accounts', label: 'Connect Accounts', isInitial: false, isFinal: false },
    { id: 'ai_preferences', label: 'AI Preferences', isInitial: false, isFinal: false },
    { id: 'completed', label: 'Completed', isInitial: false, isFinal: true },
    { id: 'skipped', label: 'Skipped', isInitial: false, isFinal: true },
  ],
  transitions: [
    { id: 't1', from: 'welcome', to: 'phone_auth', event: 'BEGIN', reversible: false, safetyTier: 2 },
    { id: 't2', from: 'phone_auth', to: 'profile', event: 'AUTHENTICATED', reversible: false, safetyTier: 1 },
    { id: 't3', from: 'profile', to: 'consent', event: 'PROFILE_SAVED', reversible: false, safetyTier: 2 },
    { id: 't4', from: 'profile', to: 'consent', event: 'SKIP', reversible: false, safetyTier: 2, guard: 'profile optional' },
    { id: 't5', from: 'consent', to: 'age_gate', event: 'CONSENT_GIVEN', reversible: false, safetyTier: 1 },
    { id: 't6', from: 'age_gate', to: 'goals', event: 'AGE_VERIFIED', reversible: false, safetyTier: 1 },
    { id: 't7', from: 'goals', to: 'connect_accounts', event: 'GOALS_SET', reversible: false, safetyTier: 2 },
    { id: 't8', from: 'goals', to: 'connect_accounts', event: 'SKIP', reversible: false, safetyTier: 2 },
    { id: 't9', from: 'connect_accounts', to: 'ai_preferences', event: 'ACCOUNTS_CONNECTED', reversible: false, safetyTier: 2 },
    { id: 't10', from: 'connect_accounts', to: 'ai_preferences', event: 'SKIP', reversible: false, safetyTier: 2 },
    { id: 't11', from: 'ai_preferences', to: 'completed', event: 'COMPLETE', reversible: false, safetyTier: 1 },
    { id: 't12', from: 'ai_preferences', to: 'completed', event: 'SKIP', reversible: false, safetyTier: 2 },
  ],
  invariants: [
    { id: 'inv1', description: 'Consent must be captured before goals and accounts steps.', applyInStates: ['goals', 'connect_accounts', 'ai_preferences'], expression: 'state.context.consentGiven === true' },
    { id: 'inv2', description: 'Age gate must be verified before proceeding to data collection steps.', applyInStates: ['goals', 'connect_accounts'], expression: 'state.context.ageVerified === true' },
  ],
};

const consentMachine: FSMDefinition = {
  id: 'consentMachine',
  label: 'Consent Machine',
  description: 'GDPR/CCPA consent management machine. Tracks consent state, version migrations, and withdrawal lifecycle.',
  sourceFile: 'src/app/machines/consentMachine.ts',
  safetyTier: 0,
  sandboxed: true,
  readOnly: false,
  emitsEvents: ['consent_granted', 'consent_withdrawn', 'consent_version_migrated'],
  complianceRefs: ['GDPR Art.7', 'GDPR Art.17', 'CCPA §1798.120', 'KOSA §3'],
  states: [
    { id: 'unconsented', label: 'No Consent', isInitial: true, isFinal: false },
    { id: 'pending_review', label: 'Pending Review', isInitial: false, isFinal: false },
    { id: 'consented', label: 'Consented', isInitial: false, isFinal: false },
    { id: 'partially_consented', label: 'Partial Consent', isInitial: false, isFinal: false },
    { id: 'withdrawn', label: 'Withdrawn', isInitial: false, isFinal: true },
    { id: 'migrating', label: 'Migrating Version', isInitial: false, isFinal: false },
  ],
  transitions: [
    { id: 't1', from: 'unconsented', to: 'pending_review', event: 'SHOW_CONSENT', reversible: false, safetyTier: 2 },
    { id: 't2', from: 'pending_review', to: 'consented', event: 'ACCEPT_ALL', reversible: false, safetyTier: 1 },
    { id: 't3', from: 'pending_review', to: 'partially_consented', event: 'ACCEPT_PARTIAL', reversible: false, safetyTier: 1, description: 'User selects individual consent categories.' },
    { id: 't4', from: 'pending_review', to: 'withdrawn', event: 'DECLINE_ALL', reversible: false, safetyTier: 1 },
    { id: 't5', from: 'consented', to: 'migrating', event: 'POLICY_UPDATED', reversible: false, safetyTier: 1 },
    { id: 't6', from: 'migrating', to: 'pending_review', event: 'MIGRATION_COMPLETE', reversible: false, safetyTier: 1 },
    { id: 't7', from: 'consented', to: 'withdrawn', event: 'WITHDRAW', reversible: false, safetyTier: 0, description: 'Triggers GDPR Art.17 erasure pipeline.' },
    { id: 't8', from: 'partially_consented', to: 'withdrawn', event: 'WITHDRAW', reversible: false, safetyTier: 0 },
  ],
  invariants: [
    { id: 'inv1', description: 'Consent record must have a version and timestamp when consented.', applyInStates: ['consented', 'partially_consented'], expression: 'state.context.version !== undefined && state.context.grantedAt !== undefined' },
    { id: 'inv2', description: 'Withdrawn state must trigger GDPR deletion event.', applyInStates: ['withdrawn'], expression: 'state.context.deletionRequested === true' },
  ],
};

// ─── Registry ─────────────────────────────────────────────────────────────────

export const FSM_REGISTRY: readonly FSMDefinition[] = [
  checkoutMachine,
  phoneOTPMachine,
  cancellationMachine,
  migrationWizardMachine,
  onboardingMachine,
  consentMachine,
];

export function getFsmById(id: string): FSMDefinition | undefined {
  return FSM_REGISTRY.find((m) => m.id === id);
}

/** All machines at a given safety tier or higher. */
export function getFsmBySafetyTier(maxTier: number): FSMDefinition[] {
  return FSM_REGISTRY.filter((m) => (m.safetyTier as number) <= maxTier);
}

/** All machines that emit a given event type. */
export function getFsmByEvent(eventType: string): FSMDefinition[] {
  return FSM_REGISTRY.filter((m) => m.emitsEvents.includes(eventType));
}

/** Simulate running through states from initial to a target state. Returns the path. */
export function findPath(
  machine: FSMDefinition,
  targetStateId: string,
): FSMTransition[] | null {
  const initialState = machine.states.find((s) => s.isInitial);
  if (!initialState) return null;
  if (initialState.id === targetStateId) return [];

  // BFS over transitions
  const visited = new Set<string>();
  const queue: Array<{ stateId: string; path: FSMTransition[] }> = [
    { stateId: initialState.id, path: [] },
  ];

  while (queue.length > 0) {
    const { stateId, path } = queue.shift()!;
    if (visited.has(stateId)) continue;
    visited.add(stateId);

    const outgoing = machine.transitions.filter((t) => t.from === stateId);
    for (const transition of outgoing) {
      const newPath = [...path, transition];
      if (transition.to === targetStateId) return newPath;
      queue.push({ stateId: transition.to, path: newPath });
    }
  }

  return null;
}
